import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {getImageUrl} from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard');
      setDashboardData(response);
    } catch (err) {
      console.error('Dashboard data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  // User must be authenticated (ProtectedRoute should handle this)
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Welcome back, {user.name}! 👋</h1>
          <p className="subtitle">
            {user.role === 'photographer'
              ? 'Manage your events and upload photos for your clients'
              : 'Discover photos where you appear using AI facial recognition'}
          </p>
        </div>

        <div className="user-profile-badge">
          <div className="profile-avatar">
            {user.profile_photo ? (
              <img src={ getImageUrl(user.profile_photo) } alt={user.name} />
            ) : (
              <div className="avatar-placeholder">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="profile-info">
            <span className="profile-name">{user.name}</span>
            <span className="profile-role">{user.role}</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Section */}
      {!loading && dashboardData && (
        <div className="dashboard-stats">
          {user.role === 'photographer' ? (
            <PhotographerStats data={dashboardData} />
          ) : (
            <UserStats data={dashboardData} />
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="stats-loading">
          <div className="spinner-small"></div>
          <p>Loading statistics...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <p>Failed to load dashboard data: {error}</p>
          <button onClick={fetchDashboardData} className="btn-retry">
            Retry
          </button>
        </div>
      )}

      {/* Quick Actions Section */}
      <div className="dashboard-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          {user.role === 'photographer' ? (
            <PhotographerActions navigate={navigate} />
          ) : (
            <UserActions navigate={navigate} user={user} />
          )}
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="dashboard-recent">
        <h2>Recent Activity</h2>
        {user.role === 'photographer' ? (
          <PhotographerActivity />
        ) : (
          <UserActivity />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER STATS COMPONENT
// ============================================================================
const PhotographerStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📅"
        label="Total Events"
        value={data.events || 0}
        color="#FF8C42"
      />
      <StatCard
        icon="📸"
        label="Total Photos"
        value={(data.photos || 0).toLocaleString()}
        color="#4CAF50"
      />
      <StatCard
        icon="💰"
        label="Revenue"
        value={`$${(data.revenue || 0).toFixed(2)}`}
        color="#2196F3"
      />
    </>
  );
};

// ============================================================================
// USER STATS COMPONENT
// ============================================================================
const UserStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📸"
        label="Photos Found"
        value={data.matches || 0}
        color="#FF8C42"
      />
      <StatCard
        icon="📅"
        label="Events"
        value={data.events || 0}
        color="#4CAF50"
      />
      <StatCard
        icon="⬇️"
        label="Downloads"
        value={data.downloads || 0}
        color="#2196F3"
      />
    </>
  );
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================
const StatCard = ({ icon, label, value, color }) => {
  return (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ color }}>
        {icon}
      </div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value" style={{ color }}>
          {value}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER QUICK ACTIONS
// ============================================================================
const PhotographerActions = ({ navigate }) => {
  return (
    <>
      <ActionCard
        icon="➕"
        title="Create Event"
        description="Start a new photography event"
        onClick={() => navigate('/events/create')}
        color="#4CAF50"
      />
      {/*}
      <ActionCard
        icon="📤"
        title="Upload Photos"
        description="Add photos to your events"
        onClick={() => navigate('/photos/upload')}
        color="#2196F3"
      />
      */}
      <ActionCard
        icon="📊"
        title="View Analytics"
        description="See your performance metrics"
        onClick={() => navigate('/analytics')}
        color="#FF8C42"
      />
      <ActionCard
        icon="⚙️"
        title="Settings"
        description="Manage your account"
        onClick={() => navigate('/settings')}
        color="#9C27B0"
      />
    </>
  );
};

// ============================================================================
// USER QUICK ACTIONS
// ============================================================================
const UserActions = ({ navigate, user }) => {
  return (
    <>
      <ActionCard
        icon="🔍"
        title="Find My Photos"
        description="Search for photos where you appear"
        onClick={() => navigate('/my-photos')}
        color="#FF8C42"
      />
      <ActionCard
        icon="📊"
        title="View Analytics"
        description="See your photo statistics"
        onClick={() => navigate('/analytics')}
        color="#4CAF50"
      />
      {!user.face_encoded && (
        <ActionCard
          icon="📷"
          title="Upload Profile Photo"
          description="Enable face recognition"
          onClick={() => navigate('/profile')}
          color="#E74C3C"
          highlight
        />
      )}
      <ActionCard
        icon="⚙️"
        title="Settings"
        description="Manage your account"
        onClick={() => navigate('/settings')}
        color="#9C27B0"
      />
    </>
  );
};

// ============================================================================
// ACTION CARD COMPONENT
// ============================================================================
const ActionCard = ({ icon, title, description, onClick, color, highlight }) => {
  return (
    <div
      className={`action-card ${highlight ? 'highlight' : ''}`}
      onClick={onClick}
      style={{ borderColor: color }}
    >
      <div className="action-icon" style={{ color }}>
        {icon}
      </div>
      <div className="action-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="action-arrow" style={{ color }}>
        →
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER RECENT ACTIVITY
// ============================================================================
const PhotographerActivity = () => {
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentEvents();
  }, []);

  const fetchRecentEvents = async () => {
    try {
      const response = await api.get('/events?limit=5');
      setRecentEvents(response.events || []);
    } catch (error) {
      console.error('Recent events error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="activity-loading">Loading recent events...</div>;
  }

  if (!recentEvents.length) {
    return (
      <div className="empty-state">
        <p>No recent events. Create your first event!</p>
      </div>
    );
  }

  return (
    <div className="activity-list">
      {recentEvents.map((event) => (
        <div key={event.id} className="activity-item">
          <div className="activity-icon">📅</div>
          <div className="activity-content">
            <h4>{event.name}</h4>
            <p>{event.category} • {new Date(event.event_date).toLocaleDateString()}</p>
          </div>
          <div className="activity-stats">
            <span>{event.photos_count || 0} photos</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// USER RECENT ACTIVITY
// ============================================================================
const UserActivity = () => {
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentMatches();
  }, []);

  const fetchRecentMatches = async () => {
    try {
      const response = await api.get('/my-photos');
      // Get first 5 photos from all events
      const allPhotos = (response.data || []).flatMap(event => 
        event.photos.map(photo => ({
          ...photo,
          event_name: event.event_name,
          event_category: event.event_category
        }))
      ).slice(0, 5);
      setRecentMatches(allPhotos);
    } catch (error) {
      console.error('Recent matches error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="activity-loading">Loading recent matches...</div>;
  }

  if (!recentMatches.length) {
    return (
      <div className="empty-state">
        <p>No photos found yet. Upload a profile photo to get started!</p>
      </div>
    );
  }

  return (
    <div className="activity-list">
      {recentMatches.map((match, index) => (
        <div key={index} className="activity-item">
          <div className="activity-icon">📸</div>
          <div className="activity-content">
            <h4>{match.event_name}</h4>
            <p>{match.event_category} • {match.confidence_score.toFixed(1)}% match</p>
          </div>
          <div className="activity-badge">
            {match.confidence_score >= 90 ? '🎯' : '✓'}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Dashboard;
